import cx from 'classnames';
import type { FC, ReactNode, SyntheticEvent } from 'react';
import { useLocalStorage } from 'react-use';

import type { IconName } from '@zen/Styleguide';
import useTracking from '@zen/utils/hooks/useTracking';
import { createTrackingEvent, createTrackingLabelAttribute, createTrackingParentAttribute } from '@zen/utils/tracking';

import Icon from '../Icon';
import { closeIconColorMapping, defaultIcon, typeIconColorMapping, typeWrapperMapping } from './helpers';
import type { BannerMessageType, BannerWidth } from './types';

interface Props {
  actions?: ReactNode;
  bannerkey?: string;
  className?: string;
  customIcon?: ReactNode;
  icon?: IconName;
  iconPosition?: 'top' | 'bottom' | 'center';
  isClosable?: boolean;
  message: ReactNode;
  onClose?: () => void;
  type?: BannerMessageType;
  width?: BannerWidth;
}

export const displayName = 'Design System/Banner';

const Banner: FC<Props> = (props) => {
  const {
    actions,
    bannerkey = '',
    className,
    customIcon,
    icon,
    iconPosition = 'center',
    isClosable = false,
    message,
    type = 'default',
    width = 'full',
    ...rest
  } = props;

  const [isBannerDisplayed, saveIsBannerDisplayed] = useLocalStorage(`banner-${bannerkey}`, true);
  const { trackEvent } = useTracking();

  const isNarrow = width === 'narrow';
  const narrowWrapperClassName: string = cx(
    {
      'p-4': !className
    },
    typeWrapperMapping[type],
    className,
    'flex flex-col gap-3 w-80 max-w-[20rem] rounded-sm'
  );

  const wideWrapperClassName: string = cx(
    'flex justify-between items-center',
    {
      'px-6 py-4 rounded-sm': !className
    },
    typeWrapperMapping[type],
    className
  );

  const iconWrapperClassName: string = cx(
    {
      'items-center': iconPosition === 'center',
      'items-start': iconPosition === 'top',
      'items-end': iconPosition === 'bottom'
    },
    'flex flex-1 min-w-0 gap-4'
  );

  if (isClosable && !isBannerDisplayed) {
    return null;
  }

  const handleClick = (event: SyntheticEvent): void => {
    const trackingEvent = createTrackingEvent(displayName, 'close', event);

    trackEvent(trackingEvent);

    saveIsBannerDisplayed(!isBannerDisplayed);
  };

  const renderIcon = (): ReactNode => {
    if (customIcon) {
      return <div className="shrink-0">{customIcon}</div>;
    }

    const iconClassNames: string = cx('shrink-0 text-base', typeIconColorMapping[type]);

    return <Icon className={iconClassNames} icon={icon || defaultIcon[type]} />;
  };

  const renderActions = (): ReactNode => {
    if (!actions) {
      return null;
    }

    return <div className="shrink-0">{actions}</div>;
  };

  const renderCloseIcon = (): ReactNode => {
    const closeIconClassNames: string = cx('cursor-pointer shrink-0', closeIconColorMapping[type]);

    return <Icon className={closeIconClassNames} icon="zicon-close" onClick={handleClick} />;
  };

  const renderMessage = (): ReactNode => {
    return (
      <div className="min-w-0 w-full" {...createTrackingLabelAttribute(type)}>
        {message}
      </div>
    );
  };

  const narrowContent: ReactNode = (
    <>
      <div className="flex justify-between items-center shrink-0">
        {renderIcon()}
        {isClosable && renderCloseIcon()}
      </div>
      {renderMessage()}
      {renderActions()}
    </>
  );

  const wideContent: ReactNode = (
    <>
      <div className={iconWrapperClassName} {...createTrackingLabelAttribute(type)}>
        {renderIcon()}
        {renderMessage()}
      </div>
      {renderActions()}
      {isClosable && renderCloseIcon()}
    </>
  );

  const classNames: string = isNarrow ? narrowWrapperClassName : wideWrapperClassName;
  const content: ReactNode = isNarrow ? narrowContent : wideContent;

  return (
    <div className={classNames} data-testid="banner-component" {...createTrackingParentAttribute('banner')} {...rest}>
      {content}
    </div>
  );
};

export type { Props as BannerProps };

export default Banner;
